// Common mapping functionality to be shared between the main map component and
// the mini map shown on individual station pages.
//
// Most of this setup involves the steps necessary for loading ESRI's basemaps
// into MapBox GL. These steps are mostly extracted from
// https://github.com/Esri/esri-leaflet-vector (which despite the name, is
// actually leveraging mapbox-gl-js via Leaflet).
export default {
  data() {
    return {
      mapLoaded: false,
      mapImageDataLoaded: false,
    };
  },

  computed: {
    mapReadyForData() {
      return (this.mapLoaded === true && this.mapImageDataLoaded === true);
    },
  },

  watch: {
    '$store.getters.mapImageData': {
      immediate: true,
      handler: 'setMapImageDataLoaded',
    },

    mapReadyForData: 'addDataToMap',
  },

  methods: {
    setMapLoaded() {
      this.mapLoaded = true;
    },

    setMapImageDataLoaded() {
      const imageData = this.$store.getters.mapImageData;
      if (imageData && Object.keys(imageData).length > 0) {
        this.mapImageDataLoaded = true;
      }
    },

    async fetchMapStyle() {
      // EASTER EGG!!!
      if (this.$route.query.trishs_wishes) {
        // https://www.arcgis.com/home/item.html?id=802841aae4dd45778801cd1d375795b9
        this.fetchAndSetMapStyle('https://tiles.arcgis.com/tiles/P3ePLMYs2RVChkJx/arcgis/rest/services/Esri_Childrens_Map/VectorTileServer/resources/styles/root.json');
      } else {
        // MapTiler custom layer.
        try {
          await this.fetchAndSetMapStyle(`https://api.maptiler.com/maps/f67b0db7-842e-43cc-8b96-343382dcaae4/style.json?key=${window.afdcStationsOptions.maptilerKey}`);
        } catch (err) {
          this.$rollbar.warning('AFDC custom MapTiler styles failed to load, falling back to default MapTiler Street styles', err);
          this.fetchAndSetMapStyle(`https://api.maptiler.com/maps/streets/style.json?key=${window.afdcStationsOptions.maptilerKey}`);
        }
      }
    },

    async fetchMapStyleUrl(url) {
      // Fetch the root style URL.
      const styleResponse = await this.$http.get(url);
      const style = styleResponse.data;

      // Patches to make ESRI styles work (only used for easter egg styles now,
      // so could probably remove if this ends up breaking).
      if (style && style.sources && style.sources.esri) {
        // Fix potential relative URLs in response.
        if (style.sources.esri.url && style.sources.esri.url.indexOf('http') === -1) {
          style.sources.esri.url = url.replace('/resources/styles/root.json', '');
        }
        if (style.glyphs.indexOf('http') === -1) {
          style.glyphs = url.replace('styles/root.json', style.glyphs.replace('../', ''));
          style.sprite = url.replace('styles/root.json', style.sprite.replace('../', ''));
        }

        // Make another request to fetch the metadata.
        const metadataResponse = await this.$http.get(style.sources.esri.url);
        const metadata = metadataResponse.data;

        // Fix potential relative URL issues.
        if (metadata.tiles && metadata.tiles[0].charAt(0) !== '/') {
          metadata.tiles[0] = `/${metadata.tiles[0]}`;
        }
        if (metadata.tileMap && metadata.tileMap.charAt(0) !== '/') {
          metadata.tileMap = `/${metadata.tileMap}`;
        }

        // Overwrite the source configuration for ESRI based on the metadata
        // fetched.
        style.sources.esri = {
          type: 'vector',
          scheme: 'xyz',
          tilejson: metadata.tilejson || '2.0.0',
          format: (metadata.tileInfo && metadata.tileInfo.format) || 'pbf',
          index: metadata.tileMap ? style.sources.esri.url + metadata.tileMap : null,
          tiles: [
            style.sources.esri.url + metadata.tiles[0],
          ],
          description: metadata.description,
          name: metadata.name,
          maxzoom: metadata.maxzoom ? metadata.maxzoom : 13,
          attribution: '<div class="afdc-map-attributions-line">Powered by <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a> | <span class="afdc-map-attributions"></span></div>',
        };
      }

      return style;
    },

    async fetchAndSetMapStyle(url) {
      // Only fetch and build the map style object once for the entire
      // application. This helps save the repeated style ajax fetches when
      // loading the mini-maps on the station show pages.
      if (!this.$root.mapStyle) {
        this.$root.mapStyle = await this.fetchMapStyleUrl(url);
      }

      this.map.setStyle(this.$root.mapStyle);
      this.map.resize();
      this.map.on('load', this.setMapLoaded.bind(this));
    },
  },
};
